import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { FolderKanban, PackageSearch, Archive, Activity, ArrowRight, CalendarClock, CheckCircle2, AlertTriangle, Clock as ClockIcon, Flame } from "lucide-react";

const Metric = ({ label, value, icon: Icon, accent = "blue" }) => {
  const border = accent === "blue" ? "border-l-blue-800" : accent === "amber" ? "border-l-amber-400" : accent === "emerald" ? "border-l-emerald-600" : "border-l-slate-400";
  return (
    <div className={`bg-white border ${border} border-l-4 border-y border-r border-slate-200 rounded-r-md p-5 shadow-sm`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <Icon size={20} className="text-slate-400" strokeWidth={2} />
      </div>
      <div className="mt-3 font-display text-4xl font-black tracking-tighter text-slate-900">{value}</div>
    </div>
  );
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [weekTotals, setWeekTotals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data));
    api.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => setLowStock([]));
    api.get("/time/summary").then((r) => setWeekTotals(r.data?.totals || [])).catch(() => setWeekTotals([]));
  }, []);

  const fmtHrs = (secs) => ((secs || 0) / 3600).toFixed(2);
  const overtimeCount = weekTotals.filter((t) => (t.seconds || 0) / 3600 > 40).length;

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title="Ops Dashboard"
        description="Live snapshot of active projects, upcoming installations, and warehouse stock."
        testId="dashboard-header"
      />
      <div className="p-4 md:p-8">
        {stats ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-metrics">
              <Metric label="Active Projects" value={stats.active_projects} icon={Activity} accent="blue" />
              <Metric label="Upcoming Projects" value={stats.upcoming_projects} icon={CalendarClock} accent="amber" />
              <Metric label="Completed" value={stats.completed_projects} icon={CheckCircle2} accent="emerald" />
              <Metric label="Archived" value={stats.archived_projects} icon={Archive} accent="slate" />
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <Metric label="Inventory Items" value={stats.inventory_items} icon={PackageSearch} />
              <Metric label="Total Stock Units" value={stats.total_stock_units} icon={PackageSearch} />
              <Metric label="Transactions Logged" value={stats.total_transactions} icon={FolderKanban} />
            </div>

            {/* Low Stock Panel */}
            <div
              className={`mt-6 border rounded-md shadow-sm ${lowStock.length ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"}`}
              data-testid="low-stock-panel"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 grid place-items-center rounded-sm ${lowStock.length ? "bg-red-600 text-white" : "bg-emerald-100 text-emerald-700"}`}>
                    {lowStock.length ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Reorder watchlist</div>
                    <div className="font-display text-xl font-black tracking-tight text-slate-900">
                      {lowStock.length === 0
                        ? "All flagged items above their reorder point"
                        : `${lowStock.length} item${lowStock.length === 1 ? "" : "s"} at or below reorder point`}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/inventory")}
                  data-testid="low-stock-open-inventory"
                  className="text-sm font-semibold text-blue-800 hover:text-blue-900 inline-flex items-center gap-1"
                >
                  Open Inventory <ArrowRight size={16} />
                </button>
              </div>
              {lowStock.length > 0 && (
                <ul className="divide-y divide-slate-200" data-testid="low-stock-list">
                  {lowStock.slice(0, 6).map((it) => {
                    const deficit = it.reorder_point - it.quantity;
                    return (
                      <li key={it.id} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`low-stock-row-${it.id}`}>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{it.name}</div>
                          <div className="font-mono text-xs text-slate-500">
                            {it.sku} · {it.category === "janitorial" ? "Janitorial" : "Office"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-lg font-bold text-red-700">{it.quantity}<span className="text-slate-400 font-normal"> / {it.reorder_point}</span></div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-red-700">
                            {deficit > 0 ? `${deficit} below` : "at threshold"}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {lowStock.length > 6 && (
                    <li className="px-5 py-2 text-xs font-mono text-slate-500">
                      +{lowStock.length - 6} more · open Inventory to see all
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Weekly Hours · overtime watch */}
            <div
              className={`mt-6 border rounded-md shadow-sm ${overtimeCount ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"}`}
              data-testid="weekly-hours-panel"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 grid place-items-center rounded-sm ${overtimeCount ? "bg-red-600 text-white animate-pulse" : "bg-blue-800 text-white"}`}>
                    {overtimeCount ? <Flame size={18} /> : <ClockIcon size={18} />}
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Weekly hours</div>
                    <div className="font-display text-xl font-black tracking-tight text-slate-900">
                      {weekTotals.length === 0
                        ? "No hours logged this week yet"
                        : overtimeCount
                          ? `${overtimeCount} teammate${overtimeCount === 1 ? " is" : "s are"} over 40 h`
                          : `${weekTotals.length} teammate${weekTotals.length === 1 ? "" : "s"} on the clock this week`}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/time")}
                  data-testid="weekly-hours-open-time"
                  className="text-sm font-semibold text-blue-800 hover:text-blue-900 inline-flex items-center gap-1"
                >
                  Open Time Clock <ArrowRight size={16} />
                </button>
              </div>
              {weekTotals.length > 0 && (
                <ul className="divide-y divide-slate-200" data-testid="weekly-hours-list">
                  {weekTotals.map((t) => {
                    const hours = (t.seconds || 0) / 3600;
                    const over = hours > 40;
                    const overBy = Math.max(0, hours - 40);
                    return (
                      <li
                        key={t.user_id}
                        className={`px-5 py-3 flex items-center justify-between gap-4 ${over ? "bg-red-50/60" : ""}`}
                        data-testid={`weekly-hours-row-${t.user_id}`}
                      >
                        <div className="min-w-0 flex items-center gap-2">
                          <div className={`font-semibold truncate ${over ? "text-red-700" : "text-slate-900"}`}>{t.user_name}</div>
                          {over && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-sm bg-red-600 text-white animate-pulse"
                              data-testid={`overtime-badge-${t.user_id}`}
                              title={`Over 40h by ${overBy.toFixed(2)}h`}
                            >
                              <Flame size={10} /> OT +{overBy.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`font-mono text-lg font-bold ${over ? "text-red-700" : "text-slate-900"}`}>
                            {fmtHrs(t.seconds)}<span className="text-slate-400 font-normal">h</span>
                          </div>
                          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            {t.user_role === "crew" ? "crew" : t.user_role?.replace(/_/g, " ")}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <button
                onClick={() => navigate("/projects")}
                data-testid="cta-open-projects"
                className="group border border-slate-200 bg-white rounded-md p-6 text-left hover:border-blue-800 transition-colors shadow-sm"
              >
                <div className="text-xs font-bold uppercase tracking-widest text-blue-800">Manage</div>
                <div className="mt-2 font-display text-2xl font-bold text-slate-900">Projects</div>
                <p className="mt-2 text-sm text-slate-600">
                  Open, edit, archive or delete projects. Row-level controls on every entry.
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-800 group-hover:gap-2 transition-all">
                  Open Projects <ArrowRight size={16} />
                </div>
              </button>
              <button
                onClick={() => navigate("/inventory")}
                data-testid="cta-open-inventory"
                className="group border border-slate-200 bg-white rounded-md p-6 text-left hover:border-blue-800 transition-colors shadow-sm"
              >
                <div className="text-xs font-bold uppercase tracking-widest text-blue-800">Stock</div>
                <div className="mt-2 font-display text-2xl font-bold text-slate-900">Warehouse Inventory</div>
                <p className="mt-2 text-sm text-slate-600">
                  Receive stock, transfer to projects, and view the full transaction log.
                </p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-800 group-hover:gap-2 transition-all">
                  Open Inventory <ArrowRight size={16} />
                </div>
              </button>
            </div>
          </>
        ) : (
          <div className="text-slate-500 font-mono text-sm">Loading dashboard…</div>
        )}
      </div>
    </div>
  );
}
